/**
 * 사이트 기본 메타·브랜드 엔티티의 단일 소스.
 *
 * 영구지침: docs/develop/seo/structured-data-spec.md §2.3, docs/develop/seo/ssr-spec.md §17.4
 *
 * 순수 모듈(`@/` 별칭 import 없음) — `npm test`(tsx --test)로 단독 실행된다.
 * 시설 수는 "3만여 개" 고정 문구다(ssr-spec §17.4.1). `32,000개` 같은 수치를 넣지 말 것.
 */
import { SITE_URL } from './absoluteUrl'

/** 기본 title — 홈(`<SEO />`)과 `index.html` 폴백이 같은 값을 쓴다 (ssr-spec §17.4.2) */
export const DEFAULT_TITLE = '케어밸런스 - 요양원·요양시설 비교 | 비용·평가등급 한눈에'

/** 기본 description — 홈(`<SEO />`)과 `index.html` 폴백이 같은 값을 쓴다 (ssr-spec §17.4.2) */
export const DEFAULT_DESCRIPTION =
  '케어밸런스 - 전국 3만여 개 요양시설 정보를 한눈에 비교하세요. 요양원 추천, 요양병원 검색, 비용 계산, 입소 상담까지. 부모님께 맞는 요양원을 찾아드립니다.'

/** 기본 og:image — `public/og-image.png` 실측 1200×630 (ssr-spec §17.4.3, siteMeta.test.ts 가 IHDR 로 고정) */
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`
export const DEFAULT_OG_IMAGE_WIDTH = 1200
export const DEFAULT_OG_IMAGE_HEIGHT = 630

/** 대표 브랜드명 — Organization·WebSite `name`, Article `publisher.name` */
export const BRAND_NAME = '케어밸런스'
/** 보조 브랜드명 — 순서 고정 */
export const BRAND_ALTERNATE_NAMES: readonly string[] = ['CareBalance', '케어밸런스(CareBalance)']
/**
 * 브랜드 로고 — 파란 로고(`_dark`). 흰 로고(`carebalance_logo.png`)는 흰 배경에서 보이지 않아 금지
 * (structured-data-spec §2.3.2). 파일이 `public/` 아래 실제로 있어야 한다(siteMeta.test.ts).
 */
export const BRAND_LOGO_URL = `${SITE_URL}/icons/carebalance_logo_dark.png`
/** 자사 네이버 스마트스토어 (`ShopRecommend.tsx` 의 `STORE_URL` 과 같은 값) */
export const BRAND_SAME_AS: readonly string[] = ['https://smartstore.naver.com/carebalance']

export const ORGANIZATION_ID = `${SITE_URL}/#organization`
export const WEBSITE_ID = `${SITE_URL}/#website`

/** Organization description — 시설 수 표기 규칙은 ssr-spec §17.4.1 */
export const ORGANIZATION_DESCRIPTION =
  '부모님 요양시설, 함께 찾아드려요. 전국 3만여 개 요양시설 정보 비교, AI 맞춤 추천, 비용 계산, 입소 상담.'

/** 홈 Organization JSON-LD (structured-data-spec §2.3.1) */
export function buildOrganizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: BRAND_NAME,
    alternateName: [...BRAND_ALTERNATE_NAMES],
    url: SITE_URL,
    logo: BRAND_LOGO_URL,
    sameAs: [...BRAND_SAME_AS],
    description: ORGANIZATION_DESCRIPTION,
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@carebalance.co.kr',
      contactType: 'customer service',
      availableLanguage: 'Korean',
    },
  }
}

/** 홈 WebSite JSON-LD (structured-data-spec §2.3.1) */
export function buildWebSiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: BRAND_NAME,
    alternateName: [...BRAND_ALTERNATE_NAMES],
    url: SITE_URL,
    inLanguage: 'ko-KR',
    publisher: { '@id': ORGANIZATION_ID },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/facilities?name={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  }
}
